import { validateDateAfter } from '@/common/validator/validatorHelpers';
import { useCrudActions } from '@/common/view/useCrudActions';
import { useMaintenanceStore } from '@/modules/maintenance/store';
import { maintenanceService } from '@/services/maintenance';
import useVuelidate from '@vuelidate/core';
import { helpers, or, requiredIf } from '@vuelidate/validators';
import { reactive } from 'vue';

export function useMaintenance() {
    const maintenanceStore = useMaintenanceStore();
    const crudAction = useCrudActions();

    const model = reactive({
        isMaintained: false,
        startTime: null,
        endTime: null,
        lastStartTime: null
    });

    const validations = {
        startTime: {
            validateDate: helpers.withMessage(
                'Value must be greater than current time',
                or(() => model.isMaintained, validateDateAfter())
            )
        },
        endTime: {
            requiredIf: requiredIf(() => model.startTime),
            validateDate: validateDateAfter('startTime')
        }
    };

    const v$ = useVuelidate(validations, model);

    const fetch = crudAction.executeAction({
        request: maintenanceService.get,
        onSuccess: ({ data }) => {
            model.startTime = model.lastStartTime = data.startTime ? new Date(data.startTime) : null;
            model.endTime = data.endTime ? new Date(data.endTime) : null;
            model.isMaintained = data.isMaintained;
            maintenanceStore.setMaintained(model.isMaintained);
        }
    });

    const actions = {
        enterMaintenance: crudAction.confirmAndExecute({
            confirmMessage: 'Are you sure you want to enter maintenance mode?',
            request: maintenanceService.enter,
            onSuccess: fetch,
            onFinally: () => v$.value.$reset()
        }),
        exitMaintenance: crudAction.confirmAndExecute({
            confirmMessage: 'Are you sure you want to exit maintenance mode?',
            request: maintenanceService.exit,
            onSuccess: fetch
        }),
        notifyMaintenance: crudAction.confirmAndExecute({
            confirmMessage: 'Are you sure you want to notify maintenance mode?',
            successMessage: 'Notify successfully!',
            validator: v$,
            request: () => {
                const startTime = model.startTime?.getTime() ?? 0;
                const endTime = model.endTime?.getTime() ?? 0;
                return maintenanceService.notify(startTime, endTime);
            },
            onSuccess: fetch
        }),
        cancelNotifyMaintenance: crudAction.confirmAndExecute({
            confirmMessage: 'Are you sure you want to cancel notification?',
            successMessage: 'Cancel notification successfully!',
            request: maintenanceService.cancelNotify,
            onSuccess: fetch,
            onFinally: () => v$.value.$reset()
        })
    };

    return { model, v$, fetch, actions };
}
